'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface UserProfile {
  name: string;
  email: string;
  phone: string;
  company: string;
  address: string;
  city: string;
  country: string;
  postalCode: string;
}

interface NotificationSettings {
  emailNotifications: boolean;
  smsNotifications: boolean;
  shipmentUpdates: boolean;
  quoteAlerts: boolean;
  marketingEmails: boolean;
}

export default function AccountSettings() {
  const [profile, setProfile] = useState<UserProfile>({
    name: 'John Doe',
    email: 'john.doe@example.com',
    phone: '+1 (555) 123-4567',
    company: 'Acme Corporation',
    address: '123 Business Street',
    city: 'New York',
    country: 'United States',
    postalCode: '10001',
  });

  const [notifications, setNotifications] = useState<NotificationSettings>({
    emailNotifications: true,
    smsNotifications: false,
    shipmentUpdates: true,
    quoteAlerts: true,
    marketingEmails: false,
  });

  const [saved, setSaved] = useState(false);

  const handleProfileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setProfile({
      ...profile,
      [e.target.name]: e.target.value,
    });
  };

  const handleNotificationToggle = (key: keyof NotificationSettings) => {
    setNotifications({
      ...notifications,
      [key]: !notifications[key],
    });
  };

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  return (
    <div className="space-y-6">
      {/* Success Message */}
      {saved && (
        <div className="bg-success/10 border border-success/20 rounded-lg p-4 flex items-center gap-3 animate-fade-in">
          <Icon name="CheckCircleIcon" size={24} className="text-success" />
          <p className="text-sm font-medium text-success">Settings saved successfully!</p>
        </div>
      )}

      {/* Profile Information */}
      <div className="bg-card rounded-xl p-6 shadow-card border border-border">
        <h2 className="text-xl font-heading font-semibold text-foreground mb-6">Profile Information</h2>
        <form onSubmit={handleSave} className="space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-foreground mb-2">
                Full Name
              </label>
              <input
                id="name"
                name="name"
                type="text"
                value={profile.name}
                onChange={handleProfileChange}
                className="w-full px-4 py-3 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
              />
            </div>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-foreground mb-2">
                Email Address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                value={profile.email}
                onChange={handleProfileChange}
                className="w-full px-4 py-3 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-foreground mb-2">
                Phone Number
              </label>
              <input
                id="phone"
                name="phone"
                type="tel"
                value={profile.phone}
                onChange={handleProfileChange}
                className="w-full px-4 py-3 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
              />
            </div>
            <div>
              <label htmlFor="company" className="block text-sm font-medium text-foreground mb-2">
                Company
              </label>
              <input
                id="company"
                name="company"
                type="text"
                value={profile.company}
                onChange={handleProfileChange}
                className="w-full px-4 py-3 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
              />
            </div>
          </div>

          <div>
            <label htmlFor="address" className="block text-sm font-medium text-foreground mb-2">
              Address
            </label>
            <input
              id="address"
              name="address"
              type="text"
              value={profile.address}
              onChange={handleProfileChange}
              className="w-full px-4 py-3 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
            />
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <div>
              <label htmlFor="city" className="block text-sm font-medium text-foreground mb-2">
                City
              </label>
              <input
                id="city"
                name="city"
                type="text"
                value={profile.city}
                onChange={handleProfileChange}
                className="w-full px-4 py-3 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
              />
            </div>
            <div>
              <label htmlFor="country" className="block text-sm font-medium text-foreground mb-2">
                Country
              </label>
              <input
                id="country"
                name="country"
                type="text"
                value={profile.country}
                onChange={handleProfileChange}
                className="w-full px-4 py-3 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
              />
            </div>
            <div>
              <label htmlFor="postalCode" className="block text-sm font-medium text-foreground mb-2">
                Postal Code
              </label>
              <input
                id="postalCode"
                name="postalCode"
                type="text"
                value={profile.postalCode}
                onChange={handleProfileChange}
                className="w-full px-4 py-3 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
              />
            </div>
          </div>

          <button
            type="submit"
            className="flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors"
          >
            <Icon name="CheckIcon" size={16} />
            Save Profile
          </button>
        </form>
      </div>

      {/* Notification Preferences */}
      <div className="bg-card rounded-xl p-6 shadow-card border border-border">
        <h2 className="text-xl font-heading font-semibold text-foreground mb-6">Notification Preferences</h2>
        <div className="space-y-4">
          <div className="flex items-center justify-between py-3 border-b border-border">
            <div>
              <h3 className="text-sm font-medium text-foreground">Email Notifications</h3>
              <p className="text-xs text-muted-foreground">Receive updates via email</p>
            </div>
            <button
              onClick={() => handleNotificationToggle('emailNotifications')}
              className={`relative w-12 h-6 rounded-full transition-colors ${
                notifications.emailNotifications ? 'bg-primary' : 'bg-muted'
              }`}
            >
              <span
                className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${
                  notifications.emailNotifications ? 'translate-x-6' : 'translate-x-0'
                }`}
              />
            </button>
          </div>

          <div className="flex items-center justify-between py-3 border-b border-border">
            <div>
              <h3 className="text-sm font-medium text-foreground">SMS Notifications</h3>
              <p className="text-xs text-muted-foreground">Receive updates via text message</p>
            </div>
            <button
              onClick={() => handleNotificationToggle('smsNotifications')}
              className={`relative w-12 h-6 rounded-full transition-colors ${
                notifications.smsNotifications ? 'bg-primary' : 'bg-muted'
              }`}
            >
              <span
                className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${
                  notifications.smsNotifications ? 'translate-x-6' : 'translate-x-0'
                }`}
              />
            </button>
          </div>

          <div className="flex items-center justify-between py-3 border-b border-border">
            <div>
              <h3 className="text-sm font-medium text-foreground">Shipment Updates</h3>
              <p className="text-xs text-muted-foreground">Get notified about shipment status changes</p>
            </div>
            <button
              onClick={() => handleNotificationToggle('shipmentUpdates')}
              className={`relative w-12 h-6 rounded-full transition-colors ${
                notifications.shipmentUpdates ? 'bg-primary' : 'bg-muted'
              }`}
            >
              <span
                className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${
                  notifications.shipmentUpdates ? 'translate-x-6' : 'translate-x-0'
                }`}
              />
            </button>
          </div>

          <div className="flex items-center justify-between py-3 border-b border-border">
            <div>
              <h3 className="text-sm font-medium text-foreground">Quote Alerts</h3>
              <p className="text-xs text-muted-foreground">Receive alerts for new quotes and expiring quotes</p>
            </div>
            <button
              onClick={() => handleNotificationToggle('quoteAlerts')}
              className={`relative w-12 h-6 rounded-full transition-colors ${
                notifications.quoteAlerts ? 'bg-primary' : 'bg-muted'
              }`}
            >
              <span
                className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${
                  notifications.quoteAlerts ? 'translate-x-6' : 'translate-x-0'
                }`}
              />
            </button>
          </div>

          <div className="flex items-center justify-between py-3">
            <div>
              <h3 className="text-sm font-medium text-foreground">Marketing Emails</h3>
              <p className="text-xs text-muted-foreground">Receive promotional offers and newsletters</p>
            </div>
            <button
              onClick={() => handleNotificationToggle('marketingEmails')}
              className={`relative w-12 h-6 rounded-full transition-colors ${
                notifications.marketingEmails ? 'bg-primary' : 'bg-muted'
              }`}
            >
              <span
                className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${
                  notifications.marketingEmails ? 'translate-x-6' : 'translate-x-0'
                }`}
              />
            </button>
          </div>
        </div>
      </div>

      {/* Security Settings */}
      <div className="bg-card rounded-xl p-6 shadow-card border border-border">
        <h2 className="text-xl font-heading font-semibold text-foreground mb-6">Security</h2>
        <div className="space-y-4">
          <button className="flex items-center gap-3 w-full px-4 py-3 bg-muted rounded-lg text-left hover:bg-muted/80 transition-colors">
            <Icon name="KeyIcon" size={20} className="text-muted-foreground" />
            <div>
              <h3 className="text-sm font-medium text-foreground">Change Password</h3>
              <p className="text-xs text-muted-foreground">Update your account password</p>
            </div>
          </button>
          <button className="flex items-center gap-3 w-full px-4 py-3 bg-muted rounded-lg text-left hover:bg-muted/80 transition-colors">
            <Icon name="ShieldCheckIcon" size={20} className="text-muted-foreground" />
            <div>
              <h3 className="text-sm font-medium text-foreground">Two-Factor Authentication</h3>
              <p className="text-xs text-muted-foreground">Add an extra layer of security</p>
            </div>
          </button>
        </div>
      </div>
    </div>
  );
}